import { array, record } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';

import { TimeUnit } from '@mews/enums';
import { keyBy } from '@mews/fp';

import { DateTimeService, DateTimeUtcService, DurationUtils, WallClockDateService } from '@mews/framework';
import { Category } from '../../../types/data';
import { toUtcWithoutMiliseconds } from '../../../utils/dates';
import { CategoryAvailability } from '../types';

const getMinNumber = (numbers: number[]) => (numbers.length > 0 ? Math.min(...numbers) : 0);

export const toEndDateUtc = (wallClockDateString: string, enterpriseTimezone: string, isDayUse: boolean): string => {
    if (isDayUse) {
        return toUtcWithoutMiliseconds(wallClockDateString, enterpriseTimezone);
    }

    return pipe(
        WallClockDateService.toTimezonedDate(
            WallClockDateService.subtractDays(WallClockDateService.parse(wallClockDateString), 1),
            enterpriseTimezone
        ),
        (date) => DateTimeService.fromDate(date),
        (date) => DateTimeService.toUtc(date),
        (date) =>
            DateTimeUtcService.toISOString(date, {
                suppressMilliseconds: true,
            })
    );
};

interface GetAvailableCategoryCountParams {
    startDateUtc: string;
    endDateUtc: string;
    timeUnitsAvailability: Record<string, number>;
}

const getValidDateUtc = (date: string, timeUnitsAvailability: Record<string, number>) => {
    if (timeUnitsAvailability[date] != null) {
        return date;
    }
    const dateTime = DateTimeUtcService.parse(date);

    const offsetDateTimeByOne = DateTimeUtcService.toISOString(DateTimeUtcService.addHours(dateTime, -1), {
        suppressMilliseconds: true,
    });

    if (timeUnitsAvailability[offsetDateTimeByOne] != null) {
        return offsetDateTimeByOne;
    }

    return DateTimeUtcService.toISOString(DateTimeUtcService.addHours(dateTime, 1), {
        suppressMilliseconds: true,
    });
};

export const getAvailableCategoryCount = ({
    startDateUtc,
    endDateUtc,
    timeUnitsAvailability,
}: GetAvailableCategoryCountParams) => {
    const validStartDateUtc = getValidDateUtc(startDateUtc, timeUnitsAvailability);
    const validEndDateUtc = getValidDateUtc(endDateUtc, timeUnitsAvailability);
    return pipe(
        timeUnitsAvailability,
        record.toArray,
        array.dropLeftWhile(([date]) => date !== validStartDateUtc),
        array.reverse,
        array.dropLeftWhile(([date]) => date !== validEndDateUtc),
        array.map(([_date, count]) => count),
        getMinNumber
    );
};

interface CreateCategoriesAvailabilityParams {
    startDate: string;
    endDate: string;
    serviceCategoriesAvailability: CategoryAvailability[];
    isDayUse: boolean;
    timezone: string;
    timeUnit: TimeUnit;
    startTimeUtc?: string;
    endTimeUtc?: string;
}

/**
 *  We Do not include the last hour in this check. We want availability TO the end date, though THROUGH.
 *  ie. Reservation from 23:00 to 01:00 should check availability for 23:00 to 00:00
 */
export const getHourlyEndDate = flow(
    DateTimeUtcService.parse,
    (date) => DateTimeUtcService.subtractHours(date, 1),
    (date) => DateTimeUtcService.toISOString(date, { suppressMilliseconds: true })
);

export const createCategoriesAvailability = ({
    startDate,
    endDate,
    serviceCategoriesAvailability,
    isDayUse,
    timezone,
    timeUnit,
    startTimeUtc,
    endTimeUtc,
}: CreateCategoriesAvailabilityParams) => {
    const durationPart = DurationUtils.timeUnitToDurationParameter(timeUnit);

    const startDateUtc = toUtcWithoutMiliseconds(startDate, timezone);
    const endDateUtc = toEndDateUtc(endDate, timezone, isDayUse);
    const isHourlyService = timeUnit === TimeUnit.Hour && startTimeUtc != null && endTimeUtc != null;

    const getDate = flow(
        (date: string) => DateTimeUtcService.parse(date),
        (date) => DateTimeService.fromUtc(date, timezone),
        DateTimeService.startOf(durationPart),
        (date) => DateTimeService.toDate(date).value,
        (date) => toUtcWithoutMiliseconds(date, timezone)
    );

    const startUtc = getDate(startDateUtc);
    const endUtc = getDate(endDateUtc);

    return pipe(
        serviceCategoriesAvailability,
        array.map(({ categoryId, timeUnitsAvailability }) => ({
            id: categoryId,
            availableCount: getAvailableCategoryCount({
                startDateUtc: isHourlyService ? startTimeUtc : startUtc,
                endDateUtc: isHourlyService ? getHourlyEndDate(endTimeUtc) : endUtc,
                timeUnitsAvailability,
            }),
        })),
        keyBy<Category>('id')
    );
};
